import { type FC, type ReactNode, useMemo } from "react";
import { isKITProduct, type ProductData } from "~/models/ProductData";
import { type NetPriceData, type PriceData } from "~/models/PriceData";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { INVALID_PACKAGE_CODE, MAX_QUANTITY } from "~/utils/constants";
import { PlainFormattedNumber } from "~/common/components/PlainFormattedNumber";
import { PriceReferenceParam } from "~/Cart/components/LineItemEditableParams";

import { getListPrice, isProductPMA } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { Permission, userHasPermission } from "~/utils/permissions";
import { multiply, volatileSum, zero } from "~/utils/PriceUtils";
import { Controller, useFormContext } from "react-hook-form";
import { type AddToCartFormData } from "~/AddToCart/hooks/useAddToCartNew";
import { useTranslation } from "react-i18next";
import { calculateSalesCampaign4PDP, useSelectPricingDetails } from "~/SalesCampaign/utils";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { Price, Price as PriceV3, type PriceProps } from "~/common/components/Price/v3/Price";
import { SaleDiscountIndicator } from "~/SalesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import classes from "./AtcProductPriceInfo.module.less";
import { IconType } from "~/common/components/icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { KitPrices } from "~/ProductDetails/components/ProductSummary/components/KitPrices";
import { InfoOrderHandlingUnitQuantity } from "~/ProductDetails/components/ProductSummary/InfoOrderHandlingUnitQuantity";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { isTariffSurchargeEnabled } from "~/Cart/cartUtils";
import { Stack, styled } from "@mui/material";
import { C_ON_PRIMARY } from "~/common/components/icon/Colors";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { SaleQuantityIndicator } from "~/SalesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import { useSalesHelper } from "~/SalesCampaign/salesHelper";
import { PriceElements } from "~/common/components/Price/PriceV4";
import { canSeeListPrice } from "~/utils/features";

interface Props {
  variant?: "ATC" | "CONTRACT";
  price: NetPriceData | null;
  priceUnit: number;
  quantity: number | null;
  disabled?: boolean;
  id?: string;
  invalidQuantity?: boolean;
  onChange?: (newQty: number) => void;
  extraParams?: { label: ReactNode; value: ReactNode }[];
  priceReference: string;
  onPriceReferenceChange?: (newValue: string | undefined) => void;
  editable?: boolean;
  descriptor: ProductDescriptor;
  isPriceLoading?: boolean;
  hasSurcharge?: boolean;
  isSpecialPrice?: boolean;
  showInfoOrderHandlingUnitQty?: boolean;
}

export const AtcProductPriceInfo = (props: Props) => {
  const {
    variant = "ATC",
    price,
    quantity,
    disabled,
    onChange,
    invalidQuantity,
    priceUnit,
    priceReference,
    onPriceReferenceChange,
    descriptor,
    isPriceLoading,
    hasSurcharge,
    showInfoOrderHandlingUnitQty,
  } = props;

  const { t } = useTranslation();
  const formProps = useFormContext<AddToCartFormData>();
  const product = descriptor.base;

  const userCanSeePrice = userHasPermission(Permission.PRICE);

  const tariffSurcharge = isTariffSurchargeEnabled() && formProps?.watch("tariffSurcharge");

  const selectedPack = formProps?.watch("selectedPack");
  const packSize = selectedPack?.packSize ?? 1;
  const packCode = selectedPack?.packCode ?? INVALID_PACKAGE_CODE;

  const isOnSale = useSalesHelper(descriptor).isPackageOnSale(packCode);
  const saleActive = isOnSale && (formProps?.watch("salePriceEnabled") ?? false);

  let priceSection;
  if (!userCanSeePrice) {
    priceSection = undefined;
  } else if (isPriceLoading) {
    priceSection = <CircleSpinner className={"mb-3"} />;
  } else if (isKITProduct(product)) {
    const optionalParts = formProps?.watch("optionalParts") ?? {};
    const selectedOptionalParts = Object.values(optionalParts).filter((item) => item.selected);

    const countTotal = (price: PriceData | null, priceUnit: number, qty: number) => {
      return !price ? null : multiply(price, qty / priceUnit);
    };

    priceSection = (
      <KitPrices
        totalPrice={countTotal(price, priceUnit, quantity ?? 1) ?? zero()}
        optionalPartsPrice={volatileSum(
          ...selectedOptionalParts.map((item) => countTotal(item.netPrice, item.priceUnit, item.quantity) ?? zero()),
        )}
        product={descriptor.base}
        includesSurcharge={hasSurcharge}
        optionalPartsIncludesSurcharge={
          selectedOptionalParts.length > 0 && selectedOptionalParts.some((item) => item.surcharge > 0)
        }
      />
    );
  } else if (variant === "CONTRACT") {
    if (price) {
      priceSection = (
        <Stack gap={2}>
          <Stack gap={0.5}>
            {/* Current price */}
            <CurrentPrice price={price} zeroText={t("product.priceOnRequest")} tariffSurcharge={tariffSurcharge} />
            {/* Price Unit */}
            <PriceV3.NetPriceUnit price={price} className={"label-medium"} />
          </Stack>
        </Stack>
      );
    }
  } else if (descriptor && !isProductPMA(descriptor)) {
    priceSection = (
      <SalePriceSection
        descriptor={descriptor}
        packCode={packCode}
        quantity={quantity ?? 1}
        priceReference={priceReference}
      />
    );
  }

  let quantitySection;
  if (quantity !== null && product.quantityVisible) {
    quantitySection = (
      <Stack gap={1}>
        <QuantitySection
          packSize={packSize}
          quantity={quantity}
          onChange={onChange}
          disabled={disabled}
          invalidQuantity={invalidQuantity}
        />
        {showInfoOrderHandlingUnitQty && (
          <InfoOrderHandlingUnitQuantity
            preferredPackQuantity={selectedPack?.preferredPackQuantity ?? null}
            packSize={selectedPack?.packSize ?? 1}
            quantity={quantity}
          />
        )}
      </Stack>
    );
  }

  const priceReferenceSection = (
    <Stack gap={1}>
      {isOnSale && formProps && (
        <Controller
          name={"salePriceEnabled"}
          render={({ field: { value, onChange } }) => (
            <CheckboxWithLabel checked={value} onCheck={onChange} hidden={!isOnSale || !!priceReference}>
              {t("feature.salesCampaign.specialDealPrice")}
            </CheckboxWithLabel>
          )}
        />
      )}
      {variant !== "CONTRACT" && (
        <PriceReferenceSection
          product={product}
          priceReference={priceReference}
          onPriceReferenceChange={onPriceReferenceChange}
          disabled={disabled || saleActive}
          editable={true} // canSeePriceRefColumn()
        />
      )}
    </Stack>
  );

  return (
    <Stack className="scha__atc-product-price-info--newVariant" gap={2} data-prod-article={product.hybrisCode}>
      {priceSection}
      {quantitySection}
      {priceReferenceSection}
    </Stack>
  );
};

export default AtcProductPriceInfo;

//////////////////////////////////////
// QUANTITY SECTION
//////////////////////////////////////
type QuantityProps = Pick<Props, "disabled" | "onChange" | "invalidQuantity"> & { quantity: number; packSize: number };
const QuantitySection: FC<QuantityProps> = ({ disabled, quantity, onChange, invalidQuantity, packSize }) => {
  const { t } = useTranslation();
  if (disabled) {
    return (
      <div className="scha__atc-product-info__quantity">
        <span className="scha__atc-product-info__quantity__label mr-1">{t("input.quantity")}</span>
        <span className="scha__atc-product-info__quantity__value">
          <PlainFormattedNumber value={quantity} />
        </span>
      </div>
    );
  }

  return (
    <StyledQuantityInput
      variant={"compact"}
      min={packSize}
      max={MAX_QUANTITY}
      value={quantity}
      disabled={disabled}
      step={packSize}
      data-test-id={"add-to-cart-input-quantity"}
      onValueChange={onChange}
      error={invalidQuantity}
      helperLabel={
        invalidQuantity && (
          <span className="error">
            {t("contractDetails.atc.quantityMismatch", {
              lower: Math.floor(quantity / packSize) * packSize,
              higher: Math.ceil(quantity / packSize) * packSize,
            })}
          </span>
        )
      }
      startAdornment={invalidQuantity && <Icon className={classes.errorIcon} type={IconType.ErrorCircle} />}
    />
  );
};

//////////////////////////////////////
// PRICE REFERENCE SECTION
//////////////////////////////////////
type PriceReferenceProps = Pick<Props, "priceReference" | "disabled" | "onPriceReferenceChange" | "editable"> & {
  product: ProductData;
};
const PriceReferenceSection: FC<PriceReferenceProps> = ({
  priceReference,
  onPriceReferenceChange,
  disabled,
  product,
  editable,
}) => {
  return (
    <StyledPriceReferenceParam
      product={product}
      priceReference={priceReference}
      // className={"mt-2"}
      onChange={onPriceReferenceChange}
      disabled={disabled}
      data-test-id={"add-to-cart-price-reference"}
      editable={editable}
      externalLabel={true}
    />
  );
};

const StyledQuantityInput = styled(QuantityInput)({
  [`& .MuiInputBase-root`]: {
    background: C_ON_PRIMARY,
  },
  [`.MuiButtonBase-root`]: {
    borderRadius: 0,
  },
});

const StyledPriceReferenceParam = styled(PriceReferenceParam)({
  [`& .MuiInputBase-root`]: {
    background: C_ON_PRIMARY,
  },
});

const CurrentPrice = styled(Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.TitleLarge),
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));

interface SalePriceSectionProps {
  descriptor: ProductDescriptor;
  packCode: string;
  quantity: number;
  priceReference: string;
}

const SalePriceSection = ({ descriptor, packCode, quantity, priceReference }: SalePriceSectionProps) => {
  const { t } = useTranslation();
  const product = descriptor.base;
  const formProps = useFormContext<AddToCartFormData>();
  const tariffSurcharge = isTariffSurchargeEnabled() && formProps?.watch("tariffSurcharge");

  const isOnSale = useSalesHelper(descriptor).isPackageOnSale(packCode);
  const saleActive = isOnSale && (formProps?.watch("salePriceEnabled") ?? false);

  const { data } = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor: descriptor }), [descriptor]);
  const saleForPack = data?.salePerPack?.[packCode];
  const { price, oldPrice, netPrice, discountFormatted } = useSelectPricingDetails({
    packCode,
    quantity,
    priceReference,
    productCode: product.code,
    salesData: saleForPack,
    saleActivated: saleActive,
  });

  const showExtraLabels = !saleActive;
  const isSoldOut = saleForPack?.soldOut ?? false;

  const somePriceValid = price?.value > 0 || (oldPrice?.value ?? 0) > 0;

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        {/* Current price */}
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          <CurrentPrice
            price={price}
            zeroText={t("product.priceOnRequest")}
            tariffSurcharge={tariffSurcharge}
            soldOut={isSoldOut}
          />

          {saleActive && (
            <SaleDiscountIndicator.V2 discountFormatted={discountFormatted} soldOut={false} variant="titleMedium" />
          )}
        </Stack>
        {/* Old price */}
        {oldPrice && <OldPrice price={oldPrice} soldOut={isSoldOut} />}
        {/* Price Unit */}
        {somePriceValid && (
          <>
            <PriceV3.NetPriceUnit price={netPrice} className={"label-medium"} />
            {tariffSurcharge && <p className="label-small mb-1">{t("price.tariff.surcharge.applicable")}</p>}
            {showExtraLabels && <PriceV3.ExtraLabels price={netPrice} surchargeEnabled={false} />}
          </>
        )}
        {/* Sale stock */}
        {saleActive && saleForPack && (
          <SaleQuantityIndicator.V2 quantity={saleForPack.salesStock} variant="labelLarge" />
        )}
      </Stack>
      {/* List price */}
      {canSeeListPrice({
        product: descriptor,
        isSpecialPrice: netPrice.isSpecial,
        hasSurcharge: netPrice.surcharge > 0,
      }) &&
        !saleActive && (
          <PriceElements.Stack>
            <PriceElements.Price
              price={getListPrice({ product: descriptor, packCode })}
              variant="titleMedium"
              fontWeight={"bold"}
            />
            <PriceElements.ListPriceLabel unit={netPrice.unit} variant="labelMedium" />
          </PriceElements.Stack>
        )}
    </Stack>
  );
};
